import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useStore } from '../../store/useStore';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { toast } from 'sonner';
import {
  Bold, Italic, Underline, List, ListOrdered, Link2, Paperclip,
  Clock, Send, X, Loader2, Minus, Flag, PenLine, Maximize2, Minimize2,
  ChevronDown, ChevronUp, Check, Cloud, CloudOff, ArrowLeft, Trash2, Sparkles,
} from 'lucide-react';
import { hora12, humanSize } from '../../lib/format';
import { DialogoConfirmar } from '../ui/confirmar';
import CampoDestinatarios from './CampoDestinatarios';
import { escaparHtml, htmlParaEditor } from '../../lib/htmlSeguro';
import { DialogoEnlace } from '../ui/enlace';
import { direccionesInvalidas, separarDirecciones } from '../../lib/destinatarios';
import { mensajeErrorRed } from '../../lib/erroresRed';
import { prometeArchivo, textoPropio } from '../../lib/avisosEnvio';
import { horaSinDia, textoProgramado } from '../../lib/programar';

const DRAFT_DEBOUNCE_MS = 700;
const DRAFT_INTERVAL_MS = 4000;
const ESPERA_DESHACER_MS = 8000;   // margen de «Deshacer» tras pulsar Enviar (M10)

/* Envíos esperando su margen de «Deshacer»: si se cierra la pestaña en ese momento, el navegador
   pregunta antes. Aunque se salga igual, el mensaje queda en Borradores (se guardó antes). */
let enviosPendientes = 0;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (enviosPendientes > 0) { e.preventDefault(); e.returnValue = ''; }
  });
}

function stripEmptyHtml(html) {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<div class="zuno-signature"[\s\S]*?<\/div>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Aurora IA en el compositor: solo reescribe lo que escribió la persona (POST /mail/aurora).
const AURORA_ACCIONES = [
  { id: 'mejorar', label: 'Mejorar redacción' },
  { id: 'corregir', label: 'Corregir ortografía' },
  { id: 'formal', label: 'Más formal' },
  { id: 'breve', label: 'Más breve' },
];

// Nodos del cuerpo que son de la persona: todo lo anterior a la firma o al correo citado.
function nodosPropios(ed) {
  const out = [];
  for (const n of Array.from(ed.childNodes)) {
    if (n.nodeType === 1 && (n.matches('.zuno-signature, blockquote') || n.querySelector('.zuno-signature, blockquote'))) break;
    out.push(n);
  }
  return out;
}

function textoAHtml(texto) {
  const esc = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return texto.trim().split(/\n{2,}/).map((p) => `<div>${esc(p).replace(/\n/g, '<br>')}</div>`).join('<div><br></div>');
}

function normalizeAccounts(accounts) {
  const arr = Array.isArray(accounts) ? accounts : [];
  return arr
    .map((a) => ({
      id: String(a?.id || a?._id || ''),
      email: a?.email || '',
      display_name: a?.display_name || a?.name || '',
      color: a?.color,
      is_default: !!a?.is_default,
    }))
    .filter((a) => a.id && a.email);
}

/**
 * Compositor Orvian: ventana flotante (escritorio) o pantalla completa (móvil).
 * El borrador se autoguarda desde el primer carácter hasta el envío.
 */
export default function Composer({ accounts, mobile }) {
  const qc = useQueryClient();
  const { composer, closeComposer, activeAccountId } = useStore();
  const data = composer.data || {};

  const editorRef = useRef(null);
  const fileRef = useRef(null);
  const draftId = useRef(data.id || null);
  const savingRef = useRef(false);
  const pendingSaveRef = useRef(false);
  const abiertoAntes = useRef(false);   // ¿este reinicio llega con el redactor ya abierto? (M14)
  const generacion = useRef(0);          // sube con cada mensaje nuevo en el redactor; un guardado viejo no pisa al nuevo
  const debounceTimer = useRef(null);
  const lastPayloadRef = useRef('');
  const closedRef = useRef(false);
  const fromRef = useRef('');
  const fromMenuRef = useRef(null);
  const inited = useRef(false);

  const accountList = useMemo(() => normalizeAccounts(accounts), [accounts]);

  const pickInitialFrom = useCallback(() => {
    const want = data.account_id || activeAccountId || accountList.find((a) => a.is_default)?.id || accountList[0]?.id;
    if (!want) return '';
    const id = String(want);
    if (accountList.some((a) => a.id === id)) return id;
    return accountList[0]?.id || '';
  }, [data.account_id, activeAccountId, accountList]);

  // —— todos los hooks de estado primero (orden estable) ——
  const [from, setFrom] = useState(() => pickInitialFrom());
  const [to, setTo] = useState((data.to || []).join(', '));
  const [cc, setCc] = useState((data.cc || []).join(', '));
  const [bcc, setBcc] = useState('');
  const [showCc, setShowCc] = useState((data.cc || []).length > 0);
  const [subject, setSubject] = useState(data.subject || '');
  const [priority, setPriority] = useState('normal');
  const [attachments, setAttachments] = useState([]);
  const [trayendoAdjuntos, setTrayendoAdjuntos] = useState(false);   // Reenviar: copiando los del original (M4)
  const [sending, setSending] = useState(false);
  // Aviso antes de enviar: { titulo, mensaje, boton, accion }
  const [avisoEnvio, setAvisoEnvio] = useState(null);
  const [confirmarDescarte, setConfirmarDescarte] = useState(false);
  const [scheduleAt, setScheduleAt] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [draftStatus, setDraftStatus] = useState('idle');
  const [minimized, setMinimized] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [toolbarOpen, setToolbarOpen] = useState(!mobile);
  const [bodyTick, setBodyTick] = useState(0);
  const [fromMenuOpen, setFromMenuOpen] = useState(false);
  const [includeSig, setIncludeSig] = useState(true);
  const [selectedSigId, setSelectedSigId] = useState(null);
  const [editorReady, setEditorReady] = useState(false);

  const { data: signatures = [] } = useQuery({
    queryKey: ['signatures'],
    queryFn: async () => (await api.get('/signatures')).data,
  });

  const selectedFromAccount = accountList.find((a) => a.id === from) || accountList[0] || null;
  const sigsForAccount = signatures.filter((s) => !s.account_id || s.account_id === from);
  const pickDefaultSig = (list) =>
    list.find((s) => s.is_default && s.account_id === from) ||
    list.find((s) => s.is_default) ||
    list[0] ||
    null;
  // La firma también pasa por el saneador del editor (R14): HTML pegado de un generador de firmas
  const sigBlock = (html) => `<div class="zuno-signature"><br><br>${htmlParaEditor(html)}</div>`;

  useEffect(() => { fromRef.current = from; }, [from]);

  // Cerrar el menú "De" al pulsar fuera / Escape
  useEffect(() => {
    if (!fromMenuOpen) return undefined;
    const onDoc = (e) => {
      if (fromMenuRef.current && !fromMenuRef.current.contains(e.target)) setFromMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setFromMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [fromMenuOpen]);

  // Móvil: bloquear scroll de fondo (sin pintar overlay negro)
  useEffect(() => {
    if (!composer.open || !mobile) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.classList.add('gmail-compose-open-mobile');
    return () => {
      document.body.style.overflow = prev;
      document.documentElement.classList.remove('gmail-compose-open-mobile');
    };
  }, [composer.open, mobile]);

  // visualViewport: solo actualiza si la altura es usable (nunca fuerza 0)
  useEffect(() => {
    if (!composer.open || !mobile || !window.visualViewport) return undefined;
    const root = document.documentElement;
    const onResize = () => {
      const h = window.visualViewport.height;
      if (h && h > 120) {
        root.style.setProperty('--compose-vv-height', `${h}px`);
        root.style.setProperty('--compose-vv-offset', `${window.visualViewport.offsetTop || 0}px`);
      }
    };
    onResize();
    window.visualViewport.addEventListener('resize', onResize);
    window.visualViewport.addEventListener('scroll', onResize);
    return () => {
      window.visualViewport.removeEventListener('resize', onResize);
      window.visualViewport.removeEventListener('scroll', onResize);
      root.style.removeProperty('--compose-vv-height');
      root.style.removeProperty('--compose-vv-offset');
    };
  }, [composer.open, mobile]);

  // Reinicio al abrir el compositor
  useEffect(() => {
    if (!composer.open) { abiertoAntes.current = false; return; }
    // Responder/Reenviar con el redactor YA abierto reemplaza lo que se estaba escribiendo (M14).
    // En este render el estado y el editor aún son los del mensaje anterior: se guarda tal cual
    // como borrador (sale en Borradores) y se dice dónde quedó, en vez de perderlo en silencio.
    if (abiertoAntes.current && !closedRef.current && hasAnyContent()) {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      const cuenta = fromRef.current || from;
      if (cuenta) {
        // Llamada propia, no saveDraft: no debe tocar draftId ni el estado del redactor nuevo.
        api.post('/drafts', payloadBorrador(cuenta))
          .then(() => {
            qc.invalidateQueries({ queryKey: ['drafts'] });
            toast.message('El mensaje que estabas escribiendo quedó guardado en Borradores.');
          })
          .catch(() => toast.error('No se pudo guardar el mensaje que estabas escribiendo.'));
      }
    }
    generacion.current += 1;
    pendingSaveRef.current = false;
    abiertoAntes.current = true;
    closedRef.current = false;
    inited.current = false;
    setEditorReady(false);
    setMinimized(false);
    setExpanded(false);
    setDraftStatus('idle');
    setSavedAt(null);
    setFromMenuOpen(false);
    lastPayloadRef.current = '';
    draftId.current = data.id || null;
    const initialFrom = pickInitialFrom();
    setFrom(initialFrom);
    fromRef.current = initialFrom;
    setTo((data.to || []).join(', '));
    setCc((data.cc || []).join(', '));
    setBcc((data.bcc || []).join(', '));
    setShowCc((data.cc || []).length > 0 || (data.bcc || []).length > 0);
    setSubject(data.subject || '');
    setAttachments(data.adjuntos || []);   // «Deshacer» y «Abrir de nuevo» devuelven también los adjuntos (M10)
    setPriority(data.priority || 'normal');
    setIncludeSig(data.includeSig !== false);
    setTrayendoAdjuntos(false);
    // Reenviar lleva los adjuntos del original (M4): el servidor los copia como si se
    // hubieran subido a mano y aquí salen como fichas que se pueden quitar.
    const pedido = data.reenviar_adjuntos;
    if (pedido?.indices?.length) {
      const gen = generacion.current;
      setTrayendoAdjuntos(true);
      api.post('/compose/forward-attachments', {
        account_id: pedido.account_id, folder: pedido.folder, uid: String(pedido.uid), indices: pedido.indices,
      })
        .then(({ data: r }) => {
          if (gen !== generacion.current) return;
          setAttachments((a) => [...(r.adjuntos || []), ...a]);
          if (r.omitidos?.length) {
            toast.warning(`No se reenvían por pesar demasiado: ${r.omitidos.map((o) => o.filename).join(', ')}. Compártelos desde Drive.`);
          }
        })
        .catch((err) => {
          if (gen === generacion.current) toast.error(`${mensajeErrorRed(err, 'traer los adjuntos del original')} Puedes adjuntarlos a mano.`);
        })
        .finally(() => { if (gen === generacion.current) setTrayendoAdjuntos(false); });
    }
    setToolbarOpen(!mobile);
    setBodyTick(0);
    // Si el editor ya está montado, inyecta el HTML nuevo. También sin HTML (mensaje nuevo): antes este
    // efecto ponía editorReady=false DESPUÉS de que el editor se montara y, sin `data.html`, no lo volvía a
    // poner en true; la firma por defecto (y el foco) nunca llegaban a un mensaje nuevo.
    if (editorRef.current) {
      inited.current = true;
      editorRef.current.innerHTML = htmlParaEditor(data.html);   // saneado: puede traer la cita de un correo ajeno
      setEditorReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composer.open, composer.mode, data?.in_reply_to, data?.subject, data?.id, data?.html]);

  /* Foco al abrir (R10): quien usa teclado o lector de pantalla no sabía que el redactor se había abierto.
     Mensaje nuevo sin destinatario → «Para»; respuesta o reenvío → el principio del cuerpo. Solo en
     escritorio: en el teléfono el foco saca el teclado encima de todo. */
  useEffect(() => {
    if (!composer.open || !editorReady || mobile) return;
    const t = setTimeout(() => {
      if (!to.trim()) { document.getElementById('composer-campo-para')?.focus(); return; }
      const ed = editorRef.current;
      if (!ed) return;
      ed.focus();
      try {
        const r = document.createRange();
        r.setStart(ed, 0);
        r.collapse(true);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(r);
      } catch (_) { /* sin selección: basta el foco */ }
    }, 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composer.open, editorReady]);

  // Cuentas que cargan tarde
  useEffect(() => {
    if (!composer.open || !accountList.length) return;
    if (!from || !accountList.some((a) => a.id === from)) {
      const next = pickInitialFrom();
      setFrom(next);
      fromRef.current = next;
    }
  }, [accountList, composer.open, from, pickInitialFrom]);

  const initEditor = useCallback((node) => {
    editorRef.current = node;
    if (node && !inited.current) {
      inited.current = true;
      node.innerHTML = htmlParaEditor(data.html);
      setEditorReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.html, composer.open]);

  useEffect(() => {
    if (!signatures.length) { setSelectedSigId(null); return; }
    // La firma que se había elegido (al deshacer un envío) manda sobre la de por defecto (R3).
    if (data.sigId && sigsForAccount.some((x) => x.id === data.sigId)) { setSelectedSigId(data.sigId); return; }
    setSelectedSigId(pickDefaultSig(sigsForAccount)?.id || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatures, from]);

  useEffect(() => {
    const ed = editorRef.current;
    if (!ed || !editorReady) return;
    // Solo la firma propia, la del primer nivel: las firmas dentro de la cita son del original (R8).
    ed.querySelectorAll(':scope > .zuno-signature').forEach((n) => n.remove());
    if (includeSig && selectedSigId) {
      const sig = signatures.find((s) => s.id === selectedSigId);
      // Encima de la cita, como en Gmail y Outlook; antes quedaba al final, debajo del original.
      const cita = ed.querySelector(':scope > blockquote');
      if (sig?.html) {
        if (cita) cita.insertAdjacentHTML('beforebegin', sigBlock(sig.html));
        else ed.insertAdjacentHTML('beforeend', sigBlock(sig.html));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSigId, includeSig, editorReady, signatures]);

  const getHtml = () => editorRef.current?.innerHTML || '';
  const getText = () => editorRef.current?.innerText || '';

  /* Texto escrito sin contar la firma propia. stripEmptyHtml la quitaba con una regex que se corta en el
     primer </div>, y las firmas tienen divs anidados: la firma sola contaba como contenido. */
  const textoSinFirma = () => {
    const ed = editorRef.current;
    if (!ed) return '';
    const copia = ed.cloneNode(true);
    copia.querySelectorAll(':scope > .zuno-signature').forEach((n) => n.remove());
    return (copia.textContent || '').replace(/\u00a0/g, ' ').trim();
  };

  const hasAnyContent = useCallback(() => {
    const text = textoSinFirma();
    return Boolean(
      (to && to.trim()) ||
      (cc && cc.trim()) ||
      (subject && subject.length > 0) ||
      text.length > 0 ||
      attachments.length > 0 ||
      draftId.current,
    );
  }, [to, cc, subject, attachments.length, bodyTick]);

  /** El borrador tal como está ahora mismo en pantalla. */
  const payloadBorrador = (accountId) => {
    const html = getHtml();
    return {
      id: draftId.current || undefined,
      account_id: accountId,
      to: separarDirecciones(to),
      cc: separarDirecciones(cc),
      bcc: separarDirecciones(bcc),
      subject: subject || '',
      html,
      text: stripEmptyHtml(html),
    };
  };

  const saveDraft = useCallback(async (opts = {}) => {
    const { force = false } = opts;
    if (closedRef.current || sending) return null;
    const accountId = fromRef.current || from;
    if (!accountId) return null;

    const payload = payloadBorrador(accountId);

    const meaningful =
      payload.to.length > 0 ||
      payload.cc.length > 0 ||
      payload.bcc.length > 0 ||
      payload.subject.length > 0 ||
      textoSinFirma().length > 0 ||
      attachments.length > 0 ||
      !!draftId.current;
    // `force` se salta «no ha cambiado», no «está vacío»: cerrar un redactor sin escribir nada guardaba
    // un borrador vacío cada vez (había 40 acumulados).
    if (!meaningful) return null;

    const sig = JSON.stringify({
      id: payload.id, to: payload.to, cc: payload.cc, bcc: payload.bcc,
      subject: payload.subject, html: payload.html, account_id: payload.account_id,
    });
    if (!force && sig === lastPayloadRef.current) return draftId.current;

    if (savingRef.current) {
      pendingSaveRef.current = true;
      return draftId.current;
    }

    savingRef.current = true;
    setDraftStatus('saving');
    const gen = generacion.current;
    try {
      const res = await api.post('/drafts', payload);
      // El redactor se reinició para otro mensaje mientras guardaba: este id ya no es el suyo.
      if (gen !== generacion.current) return null;
      if (res.data?.id) draftId.current = res.data.id;
      lastPayloadRef.current = JSON.stringify({
        id: draftId.current, to: payload.to, cc: payload.cc, bcc: payload.bcc,
        subject: payload.subject, html: payload.html, account_id: payload.account_id,
      });
      setSavedAt(new Date());
      setDraftStatus('saved');
      qc.invalidateQueries({ queryKey: ['drafts'] });
      return draftId.current;
    } catch {
      if (gen === generacion.current) setDraftStatus('error');
      return null;
    } finally {
      savingRef.current = false;
      if (pendingSaveRef.current && gen === generacion.current) {
        pendingSaveRef.current = false;
        setTimeout(() => { saveDraft({ force: true }); }, 50);
      }
    }
    // payloadBorrador solo lee to/cc/bcc/subject, que ya están aquí
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, cc, bcc, subject, attachments.length, sending, qc, bodyTick]);

  const scheduleDraftSave = useCallback(() => {
    setDraftStatus((s) => (s === 'saving' ? s : 'dirty'));
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => { saveDraft(); }, DRAFT_DEBOUNCE_MS);
  }, [saveDraft]);

  useEffect(() => {
    if (!composer.open) return undefined;
    scheduleDraftSave();
    return undefined;
  }, [to, cc, bcc, subject, from, bodyTick, composer.open, scheduleDraftSave]);

  useEffect(() => {
    if (!composer.open) return undefined;
    const t = setInterval(() => { if (hasAnyContent()) saveDraft(); }, DRAFT_INTERVAL_MS);
    return () => clearInterval(t);
  }, [composer.open, saveDraft, hasAnyContent]);

  useEffect(() => {
    if (!composer.open) return undefined;
    const flush = () => { saveDraft({ force: true }); };
    const onVis = () => { if (document.visibilityState === 'hidden') flush(); };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [composer.open, saveDraft]);

  useEffect(() => () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
  }, []);

  const onBodyInput = () => {
    setBodyTick((n) => n + 1);
    scheduleDraftSave();
  };

  const [auroraOpen, setAuroraOpen] = useState(false);
  const [auroraBusy, setAuroraBusy] = useState(false);
  const aurora = async (accion) => {
    const ed = editorRef.current;
    if (!ed || auroraBusy) return;
    setAuroraOpen(false);
    const propios = nodosPropios(ed);
    const caja = document.createElement('div');
    propios.forEach((n) => caja.appendChild(n.cloneNode(true)));
    // innerText necesita el nodo en el documento para respetar los saltos de línea
    caja.style.cssText = 'position:fixed;left:-9999px;white-space:pre-wrap';
    document.body.appendChild(caja);
    const borrador = caja.innerText.trim();
    caja.remove();
    if (!borrador) { toast.error('Escribe algo primero: Aurora mejora lo que ya hay.'); return; }
    setAuroraBusy(true);
    try {
      const { data: r } = await api.post('/mail/aurora', { accion, borrador, asunto: subject }, { timeout: 120000 });
      const cuerpo = (r?.cuerpo || '').trim();
      if (!cuerpo) throw new Error('vacio');
      if (editorRef.current !== ed) return;
      const antes = ed.innerHTML;
      nodosPropios(ed).forEach((n) => n.remove());
      ed.insertAdjacentHTML('afterbegin', textoAHtml(cuerpo));
      onBodyInput();
      toast.success('Aurora IA reescribió el mensaje', {
        duration: 8000,
        action: { label: 'Deshacer', onClick: () => { if (editorRef.current === ed) { ed.innerHTML = antes; onBodyInput(); } } },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Aurora IA no pudo responder. Intenta de nuevo.');
    } finally {
      setAuroraBusy(false);
    }
  };

  const botonIA = (
    <Popover open={auroraOpen} onOpenChange={setAuroraOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="od-ia-btn" disabled={auroraBusy} data-testid="composer-aurora" title="Aurora IA: mejorar, corregir o cambiar el tono de lo que escribiste">
          {auroraBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {auroraBusy ? 'Redactando…' : 'Redactar con IA'}
        </button>
      </PopoverTrigger>
      <PopoverContent className="z-[200] w-52 p-1" align="start" side="top">
        {AURORA_ACCIONES.map((a) => (
          <button
            key={a.id}
            type="button"
            className="w-full text-left text-sm rounded-md px-3 py-2 bg-transparent hover:bg-[var(--accent-soft)]"
            style={{ border: 'none', cursor: 'pointer', color: 'var(--text)' }}
            onClick={() => aurora(a.id)}
          >
            {a.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );

  const toggleSignature = () => setIncludeSig((v) => !v);
  const exec = (cmd) => {
    document.execCommand(cmd, false, null);
    editorRef.current?.focus();
    onBodyInput();
  };
  // Diálogo propio en vez de prompt() (M27): se guarda la selección porque el diálogo se lleva el foco.
  const [enlace, setEnlace] = useState(null);   // { rango } mientras el diálogo está abierto
  const addLink = () => {
    const sel = window.getSelection();
    const rango = sel && sel.rangeCount && editorRef.current?.contains(sel.anchorNode) ? sel.getRangeAt(0).cloneRange() : null;
    setEnlace({ rango });
  };
  const ponerEnlace = (url) => {
    const ed = editorRef.current;
    if (!ed) return;
    ed.focus();
    const sel = window.getSelection();
    if (enlace?.rango) { sel.removeAllRanges(); sel.addRange(enlace.rango); }
    // Con texto seleccionado, ese texto es el enlace; sin selección se escribe la dirección.
    if (enlace?.rango && !enlace.rango.collapsed) document.execCommand('createLink', false, url);
    else document.execCommand('insertHTML', false, `<a href="${escaparHtml(url)}">${escaparHtml(url.replace(/^mailto:/, ''))}</a>`);
    setEnlace(null);
    onBodyInput();
  };

  const uploadFiles = async (files) => {
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await api.post('/compose/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setAttachments((a) => [...a, res.data]);
        scheduleDraftSave();
      } catch (err) {
        const motivo = err?.response?.data?.detail;
        toast.error(motivo ? `${file.name}: ${motivo}` : `No se pudo subir ${file.name}`);
      }
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) uploadFiles(Array.from(e.dataTransfer.files));
  };

  const handleClose = async () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    await saveDraft({ force: true });
    closedRef.current = true;
    setFromMenuOpen(false);
    closeComposer();
  };

  /* La papelera DESCARTA (M2). Antes llamaba a handleClose y guardaba: el botón decía
     «Descartar» y el borrador seguía ahí. Cerrar (X / Atrás) sigue guardando. */
  const descartar = async () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    closedRef.current = true;          // ni el intervalo ni un guardado pendiente vuelven a crear el borrador
    pendingSaveRef.current = false;
    // Un guardado en vuelo podría estar creando el borrador ahora mismo: se espera a que acabe
    // (milisegundos) para borrar el id bueno. El redactor sigue abierto mientras tanto, así un
    // redactor nuevo no puede cambiar `draftId` por debajo.
    for (let i = 0; i < 50 && savingRef.current; i += 1) await new Promise((r) => setTimeout(r, 100));
    const id = draftId.current;
    setFromMenuOpen(false);
    closeComposer();
    if (!id) return;
    try {
      await api.delete(`/drafts/${id}`);   // el servidor quita también la copia del buzón
      toast.success('Borrador descartado');
    } catch {
      toast.error('No se pudo descartar el borrador: sigue en Borradores.');
    }
    qc.invalidateQueries({ queryKey: ['drafts'] });
    qc.invalidateQueries({ queryKey: ['unified'] });
  };

  const pedirDescartar = () => {
    if (hasAnyContent()) setConfirmarDescarte(true);
    else descartar();
  };

  const changeFromAccount = (nextId) => {
    const id = String(nextId || '');
    setFromMenuOpen(false);
    if (!id || id === from) return;
    if (!accountList.some((a) => a.id === id)) {
      toast.error('Cuenta no disponible');
      return;
    }
    setFrom(id);
    fromRef.current = id;
    lastPayloadRef.current = '';
    setDraftStatus('dirty');
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => { saveDraft({ force: true }); }, 200);
    const email = accountList.find((a) => a.id === id)?.email;
    if (email) toast.message(`Enviar desde ${email}`);
  };

  /* Los dos descuidos clásicos del correo: mandar sin asunto y prometer un archivo que no se
     adjuntó. Los dos se avisan UNA vez y se puede seguir: es un recordatorio, no un permiso. */

  const send = async (saltarAvisos = false) => {
    if (!to.trim()) { toast.error('Indica un destinatario'); return; }
    // Hora pasada: antes solo lo decía el servidor tras pulsar Programar (M25)
    if (scheduleAt && scheduleAt.getTime() <= Date.now() + 60_000) {
      toast.error('La hora programada ya pasó. Elige una más tarde o quita la programación.');
      return;
    }
    if (trayendoAdjuntos) { toast.message('Espera un momento: aún se están copiando los adjuntos del original.'); return; }
    // Antes de mandar, no después como error SMTP (M5).
    const malas = direccionesInvalidas(to, cc, bcc);
    if (malas.length) {
      toast.error(`Revisa ${malas.length === 1 ? 'esta dirección' : 'estas direcciones'}: ${malas.slice(0, 3).join(', ')}${malas.length > 3 ? '…' : ''}`);
      return;
    }
    const accountId = fromRef.current || from;
    if (!accountId) { toast.error('Elige una cuenta de envío'); return; }

    if (!saltarAvisos) {
      if (!subject.trim()) {
        setAvisoEnvio({
          titulo: '¿Enviar sin asunto?',
          mensaje: 'Un correo sin asunto es más fácil de pasar por alto y de perder en una búsqueda.',
          boton: 'Enviar así',
          accion: () => send(true),
        });
        return;
      }
      // Solo lo que escribió la persona (no la cita) y el asunto si el mensaje es nuevo (M24)
      if (!attachments.length && prometeArchivo(`${composer.mode === 'new' ? subject : ''} ${textoPropio(editorRef.current)}`)) {
        setAvisoEnvio({
          titulo: '¿Enviar sin el archivo?',
          mensaje: 'El mensaje habla de algo adjunto, pero no lleva ningún archivo.',
          boton: 'Enviar sin archivo',
          accion: () => send(true),
        });
        return;
      }
    }

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setSending(true);
    const cuerpo = {
      account_id: accountId,
      to: separarDirecciones(to),
      cc: separarDirecciones(cc),
      bcc: separarDirecciones(bcc),
      subject,
      html: getHtml(),
      text: getText(),
      priority: priority === 'normal' ? null : priority,
      in_reply_to: data.in_reply_to || null,
      attachment_ids: attachments.map((a) => a.id),
      schedule_at: scheduleAt ? scheduleAt.toISOString() : null,
    };
    // El borrador se guarda ANTES: si la pestaña se cierra durante el margen de «Deshacer»,
    // el mensaje sigue en Borradores en vez de perderse.
    try { await saveDraft({ force: true }); } catch { /* el envío no depende de esto */ }
    // Si había un guardado en vuelo, saveDraft no esperó: sin esto el id podía faltar y el
    // borrador quedarse huérfano en Borradores tras enviar (R5).
    for (let i = 0; i < 50 && savingRef.current; i += 1) await new Promise((r) => setTimeout(r, 100));
    const idBorrador = draftId.current;

    if (!scheduleAt) {
      // Envío inmediato con margen para deshacer (M10): se cierra ya y sale a los 8 s.
      const restaurar = {
        id: idBorrador || undefined, account_id: accountId,
        to: cuerpo.to, cc: cuerpo.cc, bcc: cuerpo.bcc, subject, html: cuerpo.html,
        in_reply_to: data.in_reply_to || null, adjuntos: attachments,
        priority, includeSig, sigId: selectedSigId,   // R3: «Deshacer» devuelve también esto
      };
      closedRef.current = true;
      setSending(false);
      closeComposer();
      enviarConMargen({ cuerpo, restaurar, modo: composer.mode, idBorrador });
      return;
    }

    try {
      await api.post('/compose/send', cuerpo);
      if (idBorrador) await api.delete(`/drafts/${idBorrador}`).catch(() => {});
      toast.success('Correo programado');
      despuesDeEnviar();
      closedRef.current = true;
      closeComposer();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar');
      // Si fue la contraseña, el servidor marcó la cuenta: el aviso fijo de AppShell aparece ya.
      qc.invalidateQueries({ queryKey: ['accounts'] });
    } finally {
      setSending(false);
    }
  };

  const despuesDeEnviar = () => {
    qc.invalidateQueries({ queryKey: ['messages'] });
    qc.invalidateQueries({ queryKey: ['unified'] });
    qc.invalidateQueries({ queryKey: ['unified-badge'] });
    qc.invalidateQueries({ queryKey: ['drafts'] });
    qc.invalidateQueries({ queryKey: ['scheduled'] });
    qc.invalidateQueries({ queryKey: ['accounts'] });   // un envío bueno levanta el aviso de contraseña rechazada
  };

  /* «Enviando… · Deshacer» (M10). Desde la auditoría del 2026-10-05 (R1) la espera la lleva el
     SERVIDOR: el correo entra en la cola de programados con `retener_s` y sale aunque se cierre la
     pestaña, se cierre sesión o el móvil congele la página. «Deshacer» = cancelarlo en la cola
     (atómico: si ya se estaba enviando, se dice). El aviso sigue el resultado consultando su estado. */
  const enviarConMargen = async ({ cuerpo, restaurar, modo, idBorrador }) => {
    const abrirOtraVez = () => useStore.getState().openComposer(modo, restaurar);
    const quien = cuerpo.to[0] ? `${cuerpo.to[0]}${cuerpo.to.length > 1 ? ` y ${cuerpo.to.length - 1} más` : ''}` : '';
    const fallo = (err) => {
      toast.error(err?.response?.data?.detail || err?.message || 'No se pudo enviar el correo.', {
        description: 'El mensaje sigue en Borradores.',
        duration: Infinity,
        action: { label: 'Abrir de nuevo', onClick: abrirOtraVez },
      });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['drafts'] });
    };

    let sid = null;
    enviosPendientes += 1;   // solo mientras la petición está en vuelo (después el correo ya está en el servidor)
    const aviso = toast.loading(quien ? `Enviando a ${quien}…` : 'Enviando…', { description: cuerpo.subject || '(sin asunto)' });
    try {
      const { data: r } = await api.post('/compose/send', {
        ...cuerpo, retener_s: Math.round(ESPERA_DESHACER_MS / 1000), borrador_id: idBorrador || null,
      });
      sid = r?.id || null;
      if (!r?.retenido) {   // un servidor sin retención lo envió ya
        toast.success('Mensaje enviado.', { id: aviso });
        despuesDeEnviar();
        return;
      }
    } catch (err) {
      toast.dismiss(aviso);
      fallo(err);
      return;
    } finally {
      enviosPendientes -= 1;
    }

    let deshecho = false;
    /* `type: 'default'`: el aviso nació como `toast.loading` y sonner, al actualizarlo por `id`, CONSERVA el tipo;
       un aviso «loading» no caduca nunca, así que «Enviando… · Deshacer» seguía girando después de enviado
       (QA 2026-10-06). Y el resultado reemplaza ESTE aviso (mismo `id`) en vez de apilar otro encima. */
    toast(quien ? `Enviando a ${quien}…` : 'Enviando…', {
      id: aviso,
      type: 'default',
      description: cuerpo.subject || '(sin asunto)',
      duration: ESPERA_DESHACER_MS,
      action: {
        label: 'Deshacer',
        onClick: async () => {
          try {
            await api.delete(`/scheduled/${sid}`);
            deshecho = true;
            abrirOtraVez();
            toast.message('Envío cancelado: el mensaje vuelve al redactor.', { id: aviso, type: 'default', description: undefined, action: undefined, duration: 4000 });
          } catch (err) {
            toast.error(err?.response?.status === 409
              ? 'Demasiado tarde: el correo ya se estaba enviando.'
              : mensajeErrorRed(err, 'deshacer el envío'));
          }
        },
      },
    });

    // Seguir el resultado: el servidor lo manda al acabar el margen.
    const hasta = Date.now() + ESPERA_DESHACER_MS + 90_000;
    await new Promise((r) => setTimeout(r, ESPERA_DESHACER_MS + 1500));
    while (!deshecho && Date.now() < hasta) {
      try {
        const { data: e } = await api.get(`/scheduled/${sid}/estado`);
        if (e.status === 'sent') {
          toast.success('Mensaje enviado.', { id: aviso, description: undefined, action: undefined, duration: 4000 });
          despuesDeEnviar();
          return;
        }
        if (e.status === 'failed') { toast.dismiss(aviso); fallo({ message: e.error }); return; }
        if (e.status === 'cancelled') { toast.dismiss(aviso); return; }
      } catch (_) { /* sin red un momento: se vuelve a mirar */ }
      await new Promise((r) => setTimeout(r, 2500));
    }
    if (!deshecho) {
      toast.message('El envío sigue en curso. Lo verás en Enviados en cuanto salga.',
        { id: aviso, type: 'default', description: undefined, action: undefined, duration: 6000 });
    }
  };

  const title = data.in_reply_to
    ? (composer.mode === 'forward' ? 'Reenviar' : 'Responder')
    : 'Mensaje nuevo';

  const draftLabel = (() => {
    if (sending) return 'Enviando…';
    if (draftStatus === 'saving' || draftStatus === 'dirty') return 'Guardando…';
    if (draftStatus === 'error') return 'Error al guardar · reintentando';
    if (draftStatus === 'saved' && savedAt) {
      return `Borrador guardado · ${hora12(savedAt)}`;   // 12 h, como todo el portal
    }
    return mobile ? 'Se guarda automáticamente' : null;
  })();

  const statusColor = draftStatus === 'error'
    ? 'var(--danger)'
    : draftStatus === 'saved'
      ? 'var(--good)'
      : sending
        ? 'var(--accent)'
        : 'var(--text-3)';

  if (!composer.open) return null;

  // Minimizado (solo escritorio): barra de 44px con el título
  if (!mobile && minimized) {
    return (
      <div className="gmail-compose-window gmail-compose-min" data-testid="composer">
        <div className="gmail-compose-titlebar">
          <button type="button" className="flex-1 min-w-0 text-left truncate gmail-compose-title-text" style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }} onClick={() => setMinimized(false)}>
            {title}{subject ? ` · ${subject}` : ''}
          </button>
          {draftStatus === 'saving' && <Loader2 className="h-3.5 w-3.5 od-spin shrink-0" style={{ opacity: .85 }} />}
          {draftStatus === 'saved' && <Check className="h-3.5 w-3.5 shrink-0" style={{ opacity: .85 }} />}
          <button type="button" className="gmail-compose-nav-btn" onClick={() => setMinimized(false)} aria-label="Restaurar">
            <Maximize2 className="h-4 w-4" />
          </button>
          <button type="button" className="gmail-compose-nav-btn" data-testid="composer-close" onClick={handleClose} aria-label="Cerrar">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  const shellClass = [
    'gmail-compose-window',
    mobile ? 'gmail-compose-window--mobile' : '',
    !mobile && expanded ? 'gmail-compose-window--expanded' : '',
  ].filter(Boolean).join(' ');

  const fieldInput = {
    flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none',
    color: 'var(--text)', fontSize: mobile ? 16 : 14, fontFamily: 'inherit', padding: 0,
  };

  return (
    <div
      className={shellClass}
      data-testid="composer"
      role="dialog"
      aria-label={title}
      aria-modal={mobile ? 'true' : undefined}
      onKeyDown={(e) => {
        // Ctrl/Cmd+Enter envía, como en Gmail y Outlook (M15). Pasa por los mismos avisos que el botón.
        // Los eventos de React suben también desde los diálogos en portal (enlace, avisos): solo vale
        // si la tecla se pulsó DENTRO del redactor (R7).
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.currentTarget.contains(e.target)) {
          e.preventDefault();
          if (!sending) send();
        }
      }}
    >
      <div className={`gmail-compose-titlebar ${mobile ? 'gmail-compose-titlebar--mobile' : ''}`}>
        {mobile && (
          <button type="button" className="gmail-compose-nav-btn" onClick={handleClose} aria-label="Atrás" data-testid="composer-back">
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        <span className="gmail-compose-title-text truncate">{title}</span>
        {draftLabel && (
          // En el teléfono también (M9): solo el icono de nube, y el texto si hay error al guardar.
          <span className="gmail-compose-draft-label flex items-center gap-1.5 shrink-0" data-testid="composer-draft-status" role={draftStatus === 'error' ? 'alert' : undefined} aria-label={draftLabel} title={draftLabel}>
            {draftStatus === 'saving' || draftStatus === 'dirty' || sending ? (
              <Loader2 className="h-3 w-3 od-spin shrink-0" />
            ) : draftStatus === 'error' ? (
              <CloudOff className="h-3 w-3 shrink-0" />
            ) : draftStatus === 'saved' ? (
              <Cloud className="h-3 w-3 shrink-0" />
            ) : null}
            <span className={draftStatus === 'error' ? '' : 'hidden sm:inline'}>{draftStatus === 'error' && mobile ? 'Sin guardar' : draftLabel}</span>
          </span>
        )}
        {!mobile && (
          <>
            <button
              type="button"
              className="gmail-compose-nav-btn"
              onClick={async () => { await saveDraft({ force: true }); setMinimized(true); }}
              aria-label="Minimizar"
            >
              <Minus className="h-4 w-4" />
            </button>
            <button type="button" className="gmail-compose-nav-btn" onClick={() => setExpanded((v) => !v)} aria-label={expanded ? 'Reducir' : 'Ampliar'}>
              {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
          </>
        )}
        <button type="button" className="gmail-compose-nav-btn" data-testid="composer-close" onClick={handleClose} aria-label="Cerrar">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="gmail-compose-fields">
        <div className="gmail-compose-field gmail-compose-field--from" ref={fromMenuRef}>
          <span className="gmail-compose-label">De</span>
          <div className="gmail-from-picker">
            <button
              type="button"
              className="gmail-from-trigger"
              data-testid="composer-from"
              aria-label="Cuenta de envío"
              aria-haspopup="listbox"
              aria-expanded={fromMenuOpen}
              disabled={accountList.length === 0 || sending}
              onClick={() => setFromMenuOpen((o) => !o)}
            >
              <span className="gmail-from-trigger-text">
                {selectedFromAccount ? (
                  <>
                    <span className="gmail-from-email">{selectedFromAccount.email}</span>
                    {selectedFromAccount.display_name ? (
                      <span className="gmail-from-name"> · {selectedFromAccount.display_name}</span>
                    ) : null}
                  </>
                ) : (
                  <span className="gmail-from-email">Elige una cuenta</span>
                )}
              </span>
              <ChevronDown className={`gmail-from-select-chevron h-4 w-4 ${fromMenuOpen ? 'rotate-180' : ''}`} aria-hidden />
            </button>
            {fromMenuOpen && (
              <ul className="gmail-from-menu" role="listbox" aria-label="Cuentas de envío" data-testid="composer-from-menu">
                {accountList.length === 0 && (
                  <li className="gmail-from-option gmail-from-option--empty">Sin cuentas conectadas</li>
                )}
                {accountList.map((a) => {
                  const active = a.id === from;
                  return (
                    <li key={a.id} role="option" aria-selected={active}>
                      <button
                        type="button"
                        className={`gmail-from-option ${active ? 'gmail-from-option--active' : ''}`}
                        onClick={() => changeFromAccount(a.id)}
                        data-testid={`composer-from-option-${a.id}`}
                      >
                        <span className="gmail-from-option-dot" style={{ background: a.color || 'var(--accent)' }} />
                        <span className="gmail-from-option-body">
                          <span className="gmail-from-option-email block truncate">{a.email}</span>
                          {(a.display_name || a.is_default) && (
                            <span className="gmail-from-option-meta">
                              {a.display_name || ''}
                              {a.is_default ? (a.display_name ? ' · predeterminada' : 'Predeterminada') : ''}
                            </span>
                          )}
                        </span>
                        {active && <Check className="gmail-from-option-check h-4 w-4" aria-hidden />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="gmail-compose-field">
          {/* <label> asociado: antes «Para» era un span y el campo solo tenía el placeholder como nombre (R10) */}
          <label className="gmail-compose-label" htmlFor="composer-campo-para">Para</label>
          <CampoDestinatarios
            id="composer-campo-para"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            data-testid="composer-to"
            style={fieldInput}
            placeholder="Destinatarios"
            autoComplete="email"
            /* En iOS la tecla @ solo aparece con type="email"; inputMode sola
               no la saca. `multiple` mantiene válida la lista con comas. */
            type="email"
            multiple
            inputMode="email"
          />
          {!showCc && (
            <button
              type="button"
              onClick={() => setShowCc(true)}
              className="shrink-0"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}
            >
              Cc/Cco
            </button>
          )}
        </div>
        {showCc && (
          <>
            <div className="gmail-compose-field">
              <label className="gmail-compose-label" htmlFor="composer-campo-cc">Cc</label>
              <CampoDestinatarios
                id="composer-campo-cc"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                data-testid="composer-cc"
                style={fieldInput}
                placeholder="Destinatarios en copia"
                type="email"
                multiple
                inputMode="email"
                autoComplete="email"
              />
            </div>
            <div className="gmail-compose-field">
              <label className="gmail-compose-label" htmlFor="composer-campo-cco">Cco</label>
              <CampoDestinatarios
                id="composer-campo-cco"
                value={bcc}
                onChange={(e) => setBcc(e.target.value)}
                data-testid="composer-bcc"
                style={fieldInput}
                placeholder="Copia oculta"
                type="email"
                multiple
                inputMode="email"
                autoComplete="email"
              />
            </div>
          </>
        )}
        <div className="gmail-compose-field">
          <label className="gmail-compose-label" htmlFor="composer-campo-asunto">Asunto</label>
          <input
            id="composer-campo-asunto"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="composer-subject"
            style={{ ...fieldInput, fontWeight: 600 }}
            placeholder="Asunto"
          />
        </div>
      </div>

      <div
        className={`gmail-compose-body-wrap relative ${dragOver ? 'ring-2 ring-inset' : ''}`}
        style={dragOver ? { boxShadow: 'inset 0 0 0 2px var(--accent)' } : undefined}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <div
          ref={initEditor}
          contentEditable
          suppressContentEditableWarning
          data-testid="composer-body"
          data-placeholder="Escribe tu mensaje…"
          className="gmail-compose-body od-scroll"
          onInput={onBodyInput}
          onBlur={() => saveDraft({ force: true })}
          role="textbox"
          aria-multiline="true"
          aria-label="Cuerpo del mensaje"
        />
        {dragOver && (
          <div className="absolute inset-0 grid place-items-center pointer-events-none text-sm font-semibold" style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}>
            Suelta los archivos aquí
          </div>
        )}
      </div>

      {(attachments.length > 0 || trayendoAdjuntos) && (
        <div className="gmail-compose-attachments">
          {trayendoAdjuntos && (
            <div className="gmail-compose-chip" role="status" data-testid="composer-adjuntos-reenvio">
              <Loader2 className="h-3.5 w-3.5 shrink-0 od-spin" />
              <span className="truncate flex-1">Copiando los adjuntos del original…</span>
            </div>
          )}
          {attachments.map((a) => (
            <div key={a.id} className="gmail-compose-chip">
              <Paperclip className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate flex-1">{a.filename}</span>
              <span className="shrink-0" style={{ color: 'var(--text-3)' }}>{humanSize(a.size)}</span>
              <button
                type="button"
                className="grid place-items-center shrink-0 gmail-compose-chip-quitar"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)' }}
                onClick={() => setAttachments((x) => x.filter((y) => y.id !== a.id))}
                aria-label={`Quitar ${a.filename}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {mobile && (
        <div className="flex items-center gap-1 pr-2">
          <button
            type="button"
            className="gmail-compose-toolbar-toggle gmail-compose-toolbar-toggle--labeled"
            onClick={() => setToolbarOpen((v) => !v)}
          >
            Formato {toolbarOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <div className="flex-1" />
          <div style={{ marginTop: 6 }}>{botonIA}</div>
        </div>
      )}
      {(toolbarOpen || !mobile) && (
        <div className="gmail-compose-toolbar gmail-compose-toolbar--scroll">
          {!mobile && <>{botonIA}<span className="od-sep" /></>}
          <button type="button" className="od-fmt-btn" onClick={() => exec('bold')} aria-label="Negrita" title="Negrita"><Bold className="h-4 w-4" /></button>
          <button type="button" className="od-fmt-btn" onClick={() => exec('italic')} aria-label="Cursiva" title="Cursiva"><Italic className="h-4 w-4" /></button>
          <button type="button" className="od-fmt-btn" onClick={() => exec('underline')} aria-label="Subrayado" title="Subrayado"><Underline className="h-4 w-4" /></button>
          <span className="od-sep" />
          <button type="button" className="od-fmt-btn" onClick={() => exec('insertUnorderedList')} aria-label="Lista" title="Lista"><List className="h-4 w-4" /></button>
          <button type="button" className="od-fmt-btn" onClick={() => exec('insertOrderedList')} aria-label="Lista numerada" title="Lista numerada"><ListOrdered className="h-4 w-4" /></button>
          <button type="button" className="od-fmt-btn" onClick={addLink} aria-label="Enlace" title="Enlace"><Link2 className="h-4 w-4" /></button>
          <span className="od-sep" />
          <button type="button" className="od-fmt-btn" onClick={() => fileRef.current?.click()} data-testid="composer-attach" aria-label="Adjuntar" title="Adjuntar"><Paperclip className="h-4 w-4" /></button>
          {sigsForAccount.length > 0 && (
            <button
              type="button"
              className={`od-fmt-btn ${mobile ? 'od-fmt-btn--texto' : ''}`}
              data-on={includeSig ? 'true' : 'false'}
              onClick={toggleSignature}
              data-testid="composer-signature-toggle"
              aria-label="Firma"
              aria-pressed={includeSig}
              title={includeSig ? 'Quitar firma' : 'Añadir firma'}
            >
              <PenLine className="h-4 w-4" />
              {/* M23: en táctil no hay tooltip; el icono solo no decía qué era */}
              {mobile && <span>Firma</span>}
            </button>
          )}
          {sigsForAccount.length > 1 && includeSig && (
            <Select value={selectedSigId || ''} onValueChange={setSelectedSigId}>
              <SelectTrigger className="h-8 w-auto gap-1 border-0 shadow-none text-sm shrink-0" data-testid="composer-signature-select"><SelectValue placeholder="Firma" /></SelectTrigger>
              <SelectContent className="z-[200]">{sigsForAccount.map((sg) => <SelectItem key={sg.id} value={sg.id}>{sg.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger className="h-8 w-auto gap-1 border-0 shadow-none shrink-0" data-testid="composer-priority" aria-label="Prioridad">
              <Flag className="h-4 w-4" style={priority !== 'normal' ? { color: 'var(--accent)' } : undefined} />
              {mobile && <span>{{ high: 'Prioridad alta', low: 'Prioridad baja' }[priority] || 'Prioridad'}</span>}
            </SelectTrigger>
            <SelectContent className="z-[200]">
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="high">Alta</SelectItem>
              <SelectItem value="low">Baja</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex-1 min-w-2" />
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        multiple
        className="od-file-oculto"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          uploadFiles(Array.from(e.target.files || []));
          // Sin esto, volver a elegir el MISMO archivo no dispara onChange.
          e.target.value = '';
        }}
        data-testid="composer-file-input"
      />

      <div className={`gmail-compose-footer ${mobile ? 'gmail-compose-footer--mobile' : ''}`}>
        <button type="button" className="od-btn-primary" onClick={() => send()} disabled={sending || trayendoAdjuntos} data-testid="composer-send-button">
          {sending ? <Loader2 className="h-4 w-4 od-spin" /> : <Send className="h-4 w-4" />}
          {scheduleAt ? 'Programar' : 'Enviar'}
        </button>
        {/* Adjuntar, al alcance del pulgar: en el teléfono la barra de formato
            viene plegada tras "Formato", así que el clip que vive ahí no se ve
            y parecía que no se podían mandar fotos. */}
        {mobile && (
          <button
            type="button"
            className="od-btn-secondary"
            onClick={() => fileRef.current?.click()}
            aria-label="Adjuntar"
            data-testid="composer-attach-mobile"
          >
            <Paperclip className="h-4 w-4" />
          </button>
        )}
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="od-btn-secondary" aria-label={scheduleAt ? `Cambiar la hora programada (${textoProgramado(scheduleAt)})` : 'Programar envío'} data-testid="composer-schedule">
              <Clock className="h-4 w-4" style={scheduleAt ? { color: 'var(--accent)' } : undefined} />
              {/* M23: el texto se perdía en el teléfono, justo donde no hay tooltip. Con hora elegida, dice cuándo sale
                  (antes repetía «Programar», igual que el botón de enviar; QA 2026-10-06) */}
              <span>{scheduleAt ? textoProgramado(scheduleAt) : 'Programar'}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto z-[200]" align={mobile ? 'center' : 'start'}>
            <Calendar
              mode="single"
              selected={scheduleAt}
              onSelect={(d) => {
                if (d) {
                  const dt = new Date(d);
                  const prevH = scheduleAt ? scheduleAt.getHours() : 9;
                  const prevM = scheduleAt ? scheduleAt.getMinutes() : 0;
                  dt.setHours(prevH, prevM, 0, 0);
                  setScheduleAt(dt);
                }
              }}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
            />
            <div className="p-3 space-y-2" style={{ borderTop: '1px solid var(--line)' }}>
              <label className="text-xs font-semibold" style={{ color: 'var(--text-3)' }}>Hora exacta</label>
              <input
                type="time"
                className="w-full h-10 px-3 rounded-lg text-sm"
                style={{ border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--text)' }}
                data-testid="composer-schedule-time"
                value={scheduleAt
                  ? `${String(scheduleAt.getHours()).padStart(2, '0')}:${String(scheduleAt.getMinutes()).padStart(2, '0')}`
                  : '09:00'}
                onChange={(e) => {
                  const [hh, mm] = (e.target.value || '09:00').split(':').map(Number);
                  // Sin día elegido: hoy si esa hora aún no llega, si no mañana (antes, SIEMPRE mañana)
                  if (!scheduleAt) { setScheduleAt(horaSinDia(hh, mm)); return; }
                  const base = new Date(scheduleAt);
                  base.setHours(hh || 0, mm || 0, 0, 0);
                  setScheduleAt(base);
                }}
              />
              {scheduleAt && scheduleAt.getTime() <= Date.now() + 60_000 && (
                <p role="alert" className="text-sm text-center" style={{ color: 'var(--danger)' }} data-testid="composer-programado-pasado">
                  Esa hora ya pasó: elige una más tarde.
                </p>
              )}
              {scheduleAt && (
                <div className="text-sm text-center" style={{ color: 'var(--text-2)' }}>
                  Sale {textoProgramado(scheduleAt)}
                  <button type="button" className="ml-2 font-semibold" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)' }} onClick={() => setScheduleAt(null)}>
                    Quitar
                  </button>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
        <div className="flex-1" />
        {draftLabel && (
          <span className="text-xs hidden md:inline truncate" style={{ color: statusColor, fontWeight: 500, maxWidth: 220 }}>
            {draftLabel}
          </span>
        )}
        <button type="button" className="od-tool-btn" onClick={pedirDescartar} aria-label="Descartar borrador" title="Descartar borrador" data-testid="composer-descartar">
          <Trash2 className="h-[18px] w-[18px]" />
        </button>
      </div>

      <DialogoEnlace open={!!enlace} onOpenChange={(v) => !v && setEnlace(null)} onAceptar={ponerEnlace} />
      <DialogoConfirmar
        open={confirmarDescarte}
        titulo="¿Descartar este borrador?"
        mensaje="Se borra el mensaje que estás escribiendo, también de la carpeta Borradores. Para guardarlo y seguir después, cierra con la X."
        boton="Descartar"
        testId="composer-confirmar-descarte"
        onOpenChange={setConfirmarDescarte}
        onConfirmar={descartar}
      />
      <DialogoConfirmar
        open={!!avisoEnvio}
        titulo={avisoEnvio?.titulo || ''}
        mensaje={avisoEnvio?.mensaje || ''}
        boton={avisoEnvio?.boton || 'Enviar'}
        testId="composer-aviso-envio"
        tono="normal"
        onOpenChange={(v) => !v && setAvisoEnvio(null)}
        onConfirmar={() => { const a = avisoEnvio?.accion; setAvisoEnvio(null); a?.(); }}
      />
    </div>
  );
}
